import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  HomeIcon,
  TrophyIcon,
  UserGroupIcon,
  UserCircleIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import {
  CalendarDaysIcon as CalendarDaysIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  HomeIcon as HomeIconSolid,
  TrophyIcon as TrophyIconSolid,
  UserGroupIcon as UserGroupIconSolid,
} from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import NotificationsBell from './NotificationsBell';

const isNavLinkActive = (
  pathname: string,
  to: string,
  extraPrefixes: string[] = []
): boolean => {
  if (pathname === to || pathname.startsWith(`${to}/`)) return true;
  return extraPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
};

type MobileTab = {
  to: string;
  label: string;
  title: string;
  extraPrefixes?: string[];
  home?: boolean;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  IconActive: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const MOBILE_TABS: MobileTab[] = [
  { to: '/', label: 'Home', title: 'Home', home: true, Icon: HomeIcon, IconActive: HomeIconSolid },
  {
    to: '/my-predictions',
    label: 'Predictions',
    title: 'My Predictions',
    extraPrefixes: ['/predictions'],
    Icon: ClipboardDocumentListIcon,
    IconActive: ClipboardDocumentListIconSolid,
  },
  {
    to: '/seasons',
    label: 'Fixtures',
    title: 'Competitions & Fixtures',
    Icon: CalendarDaysIcon,
    IconActive: CalendarDaysIconSolid,
  },
  {
    to: '/leagues',
    label: 'Leagues',
    title: 'Friends Leagues',
    Icon: UserGroupIcon,
    IconActive: UserGroupIconSolid,
  },
  {
    to: '/leaderboards',
    label: 'Leaderboard',
    title: 'Leaderboards',
    Icon: TrophyIcon,
    IconActive: TrophyIconSolid,
  },
];

const ThemeToggleIcon: React.FC = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="p-2 rounded-lg border transition-colors"
      style={{
        backgroundColor: 'var(--surface)',
        borderColor: 'var(--border)',
        color: 'var(--text-primary)',
      }}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {theme === 'dark' ? (
        <svg className="w-6 h-6 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg className="w-6 h-6 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </button>
  );
};

const ThemeToggleMenuItem: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const label = theme === 'dark' ? 'Light mode' : 'Dark mode';
  const MenuIcon = theme === 'dark' ? SunIcon : MoonIcon;

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5"
      style={{ color: 'var(--text-primary)' }}
    >
      <MenuIcon className="w-5 h-5 shrink-0" aria-hidden="true" />
      {label}
    </button>
  );
};

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const desktopNavLinkClass = (active: boolean) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? 'bg-highlight/15'
        : 'text-light-text dark:text-dark-text hover:text-highlight'
    }`;

  const desktopNavLinkStyle = (active: boolean, adminLink = false): React.CSSProperties =>
    active || adminLink
      ? { color: 'var(--highlight)' }
      : { color: 'var(--text-primary)' };

  useEffect(() => {
    if (user) {
      document.body.classList.add('has-mobile-bottom-nav');
    } else {
      document.body.classList.remove('has-mobile-bottom-nav');
    }
    return () => document.body.classList.remove('has-mobile-bottom-nav');
  }, [user]);

  useEffect(() => {
    setUserMenuOpen(false);
  }, [pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  const closeUserMenu = () => {
    setUserMenuOpen(false);
  };

  const isMobileTabActive = (tab: MobileTab) => {
    if (tab.home) return pathname === '/';
    return isNavLinkActive(pathname, tab.to, tab.extraPrefixes);
  };

  return (
    <>
      <nav className="relative z-40 bg-white dark:bg-dark-surface border-b border-light-border dark:border-dark-border shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0 flex items-center">
                {window.location.hostname === 'pikfc.com' ? (
                  theme === 'dark' ? (
                    <img src="/files/images/pikfc-logo.svg" alt="Pik FC" className="" style={{ width: '82px', height: 'auto' }}/>
                  ) : (
                    <img src="/files/images/pikfc-logo-black.svg" alt="Pik FC" className="" style={{ width: '82px', height: 'auto' }}/>
                  )
                ) : (
                  theme === 'dark' ? (
                    <img src="/files/images/predictorpro-logo.svg" alt="PredictorPro" className="" style={{ width: '160px', height: 'auto' }}/>
                  ) : (
                    <img src="/files/images/predictorpro-logo-black.svg" alt="PredictorPro" className="" style={{ width: '160px', height: 'auto' }}/>
                  )
                )}
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:ml-6 md:flex md:space-x-4">
                {user && (
                  <Link
                    to="/my-predictions"
                    className={desktopNavLinkClass(
                      isNavLinkActive(pathname, '/my-predictions', ['/predictions'])
                    )}
                    style={desktopNavLinkStyle(
                      isNavLinkActive(pathname, '/my-predictions', ['/predictions'])
                    )}
                  >
                    My Predictions
                  </Link>
                )}

                {user && (
                  <Link
                    to="/seasons"
                    className={desktopNavLinkClass(isNavLinkActive(pathname, '/seasons'))}
                    style={desktopNavLinkStyle(isNavLinkActive(pathname, '/seasons'))}
                  >
                    Competitions & Fixtures
                  </Link>
                )}

                {user && (
                  <Link
                    to="/leagues"
                    className={desktopNavLinkClass(isNavLinkActive(pathname, '/leagues'))}
                    style={desktopNavLinkStyle(isNavLinkActive(pathname, '/leagues'))}
                  >
                    Friends Leagues
                  </Link>
                )}

                {user && (
                  <Link
                    to="/leaderboards"
                    className={desktopNavLinkClass(isNavLinkActive(pathname, '/leaderboards'))}
                    style={desktopNavLinkStyle(isNavLinkActive(pathname, '/leaderboards'))}
                  >
                    Leaderboards
                  </Link>
                )}

                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className={desktopNavLinkClass(isNavLinkActive(pathname, '/admin'))}
                    style={desktopNavLinkStyle(isNavLinkActive(pathname, '/admin'), true)}
                  >
                    Admin
                  </Link>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 md:space-x-4">
              {user ? (
                <>
                  <NotificationsBell />
                  <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="p-2 rounded-lg border transition-colors"
                    style={{
                      backgroundColor: 'var(--surface)',
                      borderColor: 'var(--border)',
                      color: userMenuOpen ? 'var(--highlight)' : 'var(--text-primary)',
                    }}
                    aria-label="Account menu"
                    aria-expanded={userMenuOpen}
                  >
                    <UserCircleIcon className="w-6 h-6" />
                  </button>

                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={closeUserMenu} aria-hidden="true" />
                      <div
                        className="absolute right-0 z-50 mt-2 w-56 rounded-lg border shadow-lg py-2"
                        style={{
                          backgroundColor: 'var(--surface)',
                          borderColor: 'var(--border)',
                        }}
                      >
                        <div className="px-4 py-2 border-b border-light-border dark:border-dark-border">
                          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            Signed in as
                          </div>
                          <div className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                            {user.teamName}
                          </div>
                        </div>
                        <Link
                          to="/profile"
                          className="block px-4 py-2.5 text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                          style={{ color: 'var(--text-primary)' }}
                          onClick={closeUserMenu}
                        >
                          Profile
                        </Link>
                        {user.role === 'admin' && (
                          <Link
                            to="/admin"
                            className="md:hidden block px-4 py-2.5 text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                            style={{ color: 'var(--highlight)' }}
                            onClick={closeUserMenu}
                          >
                            Admin
                          </Link>
                        )}
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          Logout
                        </button>
                        <div className="mt-1 pt-1 border-t border-light-border dark:border-dark-border">
                          <ThemeToggleMenuItem />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                </>
              ) : (
                <>
                  <ThemeToggleIcon />
                  <Link
                    to="/login"
                    className="btn-secondary px-3 py-2 rounded-md text-md md:text-sm font-medium transition-colors"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="hidden md:block btn-primary text-sm"
                  >
                    Create Account
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile bottom tab bar */}
      {user && (
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 z-50 w-full max-w-[100vw] border-t border-light-border dark:border-dark-border shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
          style={{
            backgroundColor: 'var(--surface)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
          aria-label="Main navigation"
        >
          <div className="flex items-stretch justify-around h-16 w-full max-w-full mx-auto">
            {MOBILE_TABS.map((tab) => {
              const active = isMobileTabActive(tab);
              const Icon = active ? tab.IconActive : tab.Icon;
              return (
                <Link
                  key={tab.to}
                  to={tab.to}
                  title={tab.title}
                  className="flex flex-1 flex-col items-center justify-center gap-0.5 min-w-0 px-1 transition-colors"
                  style={{ color: active ? 'var(--highlight)' : 'var(--text-secondary)' }}
                >
                  <Icon className="w-6 h-6 shrink-0" aria-hidden="true" />
                  <span className="text-[10px] font-medium leading-tight truncate max-w-full">
                    {tab.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </>
  );
};

export default Navbar;
